import { useEffect, useRef, useState, type FormEvent } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactBubbleModal from '../components/ContactBubbleModal';

gsap.registerPlugin(ScrollTrigger);

function RippleInput({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
}: {
  label: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div className="relative mb-8">
      <label className="text-caption block mb-2" style={{ color: '#6e5e50' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="relative z-10 w-full bg-transparent py-3 text-sm outline-none"
        style={{
          border: 'none',
          borderBottom: '1px solid #151414',
          color: '#a88868',
        }}
      />
    </div>
  );
}

export default function CustomSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const letterRef = useRef<HTMLDivElement>(null);

  const [orgType, setOrgType] = useState('');
  const [orgName, setOrgName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [orgScale, setOrgScale] = useState('');
  const [painPoint, setPainPoint] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const orgTypes = [
    { value: 'enterprise', label: '企事业单位' },
    { value: 'school', label: '学校/教育机构' },
    { value: 'community', label: '社区/公益组织' },
    { value: 'other', label: '其他' },
  ];

  const scales = [
    { value: '1-50', label: '1-50人' },
    { value: '50-200', label: '50-200人' },
    { value: '200-1000', label: '200-1000人' },
    { value: '1000+', label: '1000人以上' },
  ];

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setOrgType('');
      setOrgName('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setOrgScale('');
      setPainPoint('');
    }, 3000);
  };

  useEffect(() => {
    const letter = letterRef.current;
    if (!letter) return;

    gsap.from(letter, {
      y: 60,
      opacity: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: letter,
        start: 'top bottom-=10%',
        toggleActions: 'play none none reverse',
      },
    });
  }, []);

  return (
    <section
      id="custom"
      ref={sectionRef}
      className="relative"
      style={{
        backgroundColor: '#fcfcfc',
        padding: '10vmax 5vmax',
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
      />

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Letter */}
          <div ref={letterRef}>
            {/* Subtle warm accent */}
            <div
              className="mb-3"
              style={{
                width: '6px',
                height: '1px',
                backgroundColor: 'rgba(200, 170, 130, 0.4)',
              }}
            />
            <p className="text-caption text-umber mb-4">定制服务</p>
            <h2
              className="font-serif mb-6"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                fontWeight: 400,
                lineHeight: 1.2,
                color: '#a88868',
                textShadow: '1px 1px 0 rgba(255,255,255,0.5), 2px 2px 0 rgba(180,160,130,0.15)',
              }}
            >
              告诉我们您的需求
            </h2>

            {/* Business Scope Table - compact */}
            <div
              className="mb-6 relative overflow-hidden"
              style={{
                backgroundColor: '#faf6f0',
                border: '1px solid rgba(200, 170, 130, 0.15)',
                borderRadius: '2px',
              }}
            >
              <table className="w-full" style={{ fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(200, 170, 130, 0.2)' }}>
                    <th className="text-left py-2.5 px-3 font-medium" style={{ color: '#a88868', width: '22%' }}>业务板块</th>
                    <th className="text-left py-2.5 px-3 font-medium" style={{ color: '#a88868', width: '26%' }}>核心职能</th>
                    <th className="text-left py-2.5 px-3 font-medium" style={{ color: '#a88868', width: '28%' }}>细分领域</th>
                    <th className="text-left py-2.5 px-3 font-medium" style={{ color: '#a88868', width: '24%' }}>业务负责</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { dept: '心理咨询中心', func: '个案咨询与治疗', tag: '一对一干预 · 情绪疏导', owner: '乐恩 心璞' },
                    { dept: '影子老师团队', func: '入校适应力支持', tag: '校园陪读 · 社交/规则建立', owner: '乐恩 内观心' },
                    { dept: '职场心理服务', func: '组织效率与成长', tag: '企业EAP · 领导力心理建设', owner: '乐恩 孔隙' },
                    { dept: '专题学术研究中心', func: '垂直领域攻坚', tag: '躯体化 · 游戏成瘾 · 梦境心理学', owner: '心璞+内观心+孔隙' },
                    { dept: '有心AI运营服务部', func: '非技术人员AI成长', tag: 'AI工具赋能 · 降本增效实操', owner: '乐恩 孔隙' },
                  ].map((row, i) => (
                    <tr
                      key={row.dept}
                      style={{
                        borderBottom: i < 4 ? '1px solid rgba(200, 170, 130, 0.1)' : 'none',
                      }}
                    >
                      <td className="py-2.5 px-3 font-medium" style={{ color: '#5a4a3a' }}>{row.dept}</td>
                      <td className="py-2.5 px-3" style={{ color: '#6e5e50' }}>{row.func}</td>
                      <td className="py-2.5 px-3" style={{ color: '#8a7a68', fontSize: '0.72rem' }}>{row.tag}</td>
                      <td className="py-2.5 px-3" style={{ color: '#9a7a5a', fontSize: '0.72rem' }}>{row.owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Partner Letter */}
            <div
              className="p-6 lg:p-8 relative"
              style={{
                backgroundColor: '#faf6f0',
                border: '1px solid rgba(200, 170, 130, 0.15)',
                borderRadius: '2px',
              }}
            >
              <div
                className="absolute top-0 left-0 w-px h-full"
                style={{
                  backgroundColor: 'rgba(21, 20, 20, 0.08)',
                  transformOrigin: 'top center',
                  animation: 'line-expand-v 1.5s ease-out forwards',
                }}
              />

              <p
                className="mb-6"
                style={{
                  fontSize: '0.92rem',
                  lineHeight: 1.8,
                  color: '#6e5e50',
                  fontFamily: "'Noto Serif SC', serif",
                }}
              >
                尊敬的合作伙伴：
              </p>
              <p
                className="mb-4"
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.8,
                  color: '#6e5e50',
                }}
              >
                无论您是希望为团队引入心理健康服务的企业HR，关注学生心理成长的学校管理者，还是致力于社区心理建设的公益组织——我们都将为您提供最契合的解决方案。
              </p>
              <p
                className="mb-4"
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.8,
                  color: '#6e5e50',
                }}
              >
                填写右侧表单，我们的专业顾问将在24小时内与您联系，根据您的组织类型、规模和核心痛点，出具定制化的《心理健康与效能提升方案》。
              </p>
              <div
                className="mb-6"
                style={{
                  backgroundColor: 'rgba(184, 152, 80, 0.08)',
                  border: '1.5px solid rgba(184, 152, 80, 0.25)',
                  borderRadius: '2px',
                  padding: '16px 20px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-1px',
                    left: '20px',
                    width: '40px',
                    height: '2px',
                    backgroundColor: '#b89850',
                  }}
                />
                <p
                  style={{
                    fontSize: 'clamp(1.05rem, 2vw, 1.35rem)',
                    lineHeight: 1.6,
                    color: '#9a7a40',
                    fontFamily: "'Noto Serif SC', serif",
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                  }}
                >
                  首次合作，可享受一次公益价主题课程，时长1.5小时
                </p>
              </div>
              <div
                className="mt-2 mb-1"
                style={{
                  padding: '12px 16px',
                  backgroundColor: 'rgba(184, 152, 80, 0.06)',
                  borderLeft: '3px solid #b89850',
                  borderRadius: '0 3px 3px 0',
                }}
              >
                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
                    lineHeight: 1.5,
                    color: '#5a4a3a',
                    fontFamily: "'Noto Serif SC', serif",
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                  }}
                >
                  乐恩心理事务所
                </p>
                <p
                  className="mt-1"
                  style={{
                    fontSize: '0.78rem',
                    color: '#8a7a68',
                    letterSpacing: '0.06em',
                  }}
                >
                  孔隙心理 X 有心AI 运营服务部
                </p>
              </div>
              {/* Handwritten PS note */}
              <p
                className="mt-4"
                style={{
                  fontSize: '0.92rem',
                  lineHeight: 1.6,
                  color: 'rgba(110, 94, 80, 0.4)',
                  fontFamily: "'Noto Serif SC', serif",
                  fontStyle: 'italic',
                }}
              >
                P.S. 每一个愿意关注内心的人，都值得被认真对待
              </p>

              {/* Contact CTA button */}
              <button
                onClick={() => setShowModal(true)}
                className="interactive mt-8 flex items-center gap-3 px-8 py-3.5"
                style={{
                  background: 'linear-gradient(135deg, #c49a3c 0%, #d4a84c 50%, #b89438 100%)',
                  color: '#fff',
                  borderRadius: '9999px',
                  fontSize: '0.92rem',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(196, 154, 60, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.04)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(196, 154, 60, 0.45), inset 0 1px 0 rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(196, 154, 60, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px) scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.04)';
                }}
              >
                <span
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>联系我们</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '4px' }}>→</span>
              </button>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: 'rgba(200, 170, 130, 0.1)',
                    border: '1px solid rgba(200, 170, 130, 0.2)',
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c07840" strokeWidth="1.5">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-serif mb-2" style={{ fontSize: '1.15rem', color: '#151414' }}>
                  感谢您的信任
                </h3>
                <p style={{ fontSize: '0.9rem', color: '#6e5e50', textAlign: 'center', lineHeight: 1.6 }}>
                  我们将在24小时内联系您
                </p>
                <div
                  className="mt-5 px-4 py-2 flex items-center gap-2"
                  style={{
                    backgroundColor: 'rgba(21, 20, 20, 0.03)',
                    border: '1px solid rgba(21, 20, 20, 0.06)',
                    borderRadius: '9999px',
                  }}
                >
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ade80' }} />
                  <span style={{ fontSize: '0.75rem', color: '#6e5e50', letterSpacing: '0.05em' }}>
                    客服已收到您的提交
                  </span>
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="interactive mt-5 flex items-center gap-2 px-5 py-2.5 transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: '#2c8a3a',
                    color: '#fff',
                    borderRadius: '9999px',
                    fontSize: '0.92rem',
                    letterSpacing: '0.05em',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                  咨询在线客服
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-8">
                  <label className="text-caption block mb-3" style={{ color: '#6e5e50' }}>
                    组织类型 *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {orgTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setOrgType(type.value)}
                        className="interactive px-4 py-2 text-caption transition-all duration-300"
                        style={{
                          border: '1px solid',
                          borderColor: orgType === type.value ? '#c8a878' : 'rgba(200, 170, 130, 0.25)',
                          backgroundColor: orgType === type.value ? '#f0e6d4' : 'transparent',
                          color: orgType === type.value ? '#5a4a3a' : '#6e5e50',
                          borderRadius: '9999px',
                        }}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                <RippleInput
                  label="组织名称 *"
                  placeholder="请输入您的组织全称"
                  value={orgName}
                  onChange={setOrgName}
                  required
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <RippleInput
                    label="联系人姓名 *"
                    placeholder="您的姓名"
                    value={contactName}
                    onChange={setContactName}
                    required
                  />
                  <RippleInput
                    label="联系电话 *"
                    type="tel"
                    placeholder="手机号码"
                    value={contactPhone}
                    onChange={setContactPhone}
                    required
                  />
                </div>

                <RippleInput
                  label="电子邮箱"
                  type="email"
                  placeholder="工作邮箱（选填）"
                  value={contactEmail}
                  onChange={setContactEmail}
                />

                <div className="mb-8">
                  <label className="text-caption block mb-3" style={{ color: '#6e5e50' }}>
                    组织规模
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {scales.map((scale) => (
                      <button
                        key={scale.value}
                        type="button"
                        onClick={() => setOrgScale(scale.value)}
                        className="interactive px-4 py-2 text-caption transition-all duration-300"
                        style={{
                          border: '1px solid',
                          borderColor: orgScale === scale.value ? '#c8a878' : 'rgba(200, 170, 130, 0.25)',
                          backgroundColor: orgScale === scale.value ? '#f0e6d4' : 'transparent',
                          color: orgScale === scale.value ? '#5a4a3a' : '#6e5e50',
                          borderRadius: '9999px',
                        }}
                      >
                        {scale.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative mb-8">
                  <label className="text-caption block mb-2" style={{ color: '#6e5e50' }}>
                    核心痛点 / 需求描述
                  </label>
                  <textarea
                    value={painPoint}
                    onChange={(e) => setPainPoint(e.target.value)}
                    placeholder="请简要描述您当前面临的主要挑战或期望解决的问题..."
                    rows={4}
                    className="w-full py-3 text-sm outline-none resize-none px-3"
                    style={{
                      border: 'none',
                      borderBottom: '1px solid rgba(200, 170, 130, 0.4)',
                      backgroundColor: 'rgba(250, 246, 240, 0.5)',
                      color: '#151414',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  className="interactive w-full py-4 text-caption transition-all duration-300 hover:opacity-90"
                  style={{
                    backgroundColor: '#151414',
                    color: '#fcfcfc',
                    borderRadius: '2px',
                  }}
                >
                  提交需求，获取定制方案
                </button>

                <p
                  className="mt-4 text-center"
                  style={{ fontSize: '0.78rem', color: 'rgba(110, 94, 80, 0.6)' }}
                >
                  提交即表示您同意我们的隐私政策，我们将严格保护您的信息安全
                </p>
              </form>
            )}
          </div>

          {/* Contact Bubble Modal */}
          <ContactBubbleModal open={showModal} onClose={() => setShowModal(false)} />
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }}
      />
    </section>
  );
}
